import type * as ClassTypes from './types/class';
import {
    initAdaptive,
    initCallbackPopup,
    initCustomSelects,
    initDownload,
    initForm,
    initHeader,
    initMenu,
    initPopup,
    initProjects,
    initRotation,
    initShare,
    initSimilarPremises,
    initSlider,
    initTabs,
    initVideo,
    lazyLoadElements
} from './common';
import type {IDynamicImport} from './types/utils';
import type {IObserver} from '@/common/scripts/types/observer';
import Observer from './observer';
import {Utils} from '@/common/scripts/utils';

const observer: IObserver = new Observer();

// Поведение шапки
const headerWrap = document.querySelector<HTMLElement>('.j-header');
const headerWrapper = headerWrap || document.querySelector('header') as HTMLElement;

if (headerWrap) {
    initHeader(headerWrap);

    const menuWrapper = headerWrap.querySelector<HTMLElement>('.j-menu');
    const openButton = headerWrap.querySelector<HTMLElement>('.j-header-menu-button');

    if (menuWrapper && openButton) {
        initMenu({
            wrapper: menuWrapper,
            headerWrap,
            openButton
        });
    }
}

const firstScreenSlider = [...document.querySelectorAll<HTMLElement>('.j-first-screen-slider')];

firstScreenSlider.forEach((slider: HTMLElement) => {
    const sliderOptions = {
        loop         : true,
        effect       : 'fade',
        autoplay     : Boolean(slider.dataset['autoplay'] === 'true'),
        slidesPerView: 1,
        spaceBetween : 0,
        speed        : 800,
        pagination   : {
            el       : '.j-first-screen-mobile-pagination',
            clickable: true
        },
        navigation: {
            nextEl: '.j-first-screen-arrow-next',
            prevEl: '.j-first-screen-arrow-prev'
        },
        initFlag: true
    };

    initSlider(slider, {sliderOptions});
});

const flatPromoSliders = [...document.querySelectorAll<HTMLElement>('.j-flat-promo-slider')];

flatPromoSliders.forEach((flatPromoSlider: HTMLElement) => {
    const sliderOptions = {
        loop         : true,
        setDisabled  : true,
        slidesPerView: 1,
        spaceBetween : 0,
        navigation   : {
            nextEl: '.j-flat-promo-arrow-next',
            prevEl: '.j-flat-promo-arrow-prev'
        }
    };

    initSlider(flatPromoSlider, {sliderOptions});
});

const setVH = (): void => {
    // First we get the viewport height and we multiple it by 1% to get a value for a vh unit
    let vh = window.innerHeight * 0.01;

    // Then we set the value in the --vh custom property to the root of the document
    document.documentElement.style.setProperty('--vh', `${vh}px`);

    // We listen to the resize event
    window.addEventListener('resize', () => {
    // We execute the same script as before
        vh = window.innerHeight * 0.01;

        document.documentElement.style.setProperty('--vh', `${vh}px`);
    });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        setVH();
    });
} else {
    setVH();
}

/* Ленивая загрузка */
/* подключать после инициализации слайдеров */
const lazyElements = [...document.querySelectorAll('[loading=lazy]')];

if (lazyElements.length) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            lazyLoadElements(lazyElements);
        });
    } else {
        lazyLoadElements(lazyElements);
    }
}

// Уведомление об использовании cookies
const cookiesWrapper = document.querySelector('.j-cookies-notice') as HTMLElement;

if (cookiesWrapper) {
    import(/* webpackChunkName: "cookies-notice" */ '@/components/cookies-notice')
        .then(({default: CookiesNotice}: IDynamicImport) => {
            const cookiesNotice: ClassTypes.ICookiesNotice = new CookiesNotice();

            cookiesNotice.init(cookiesWrapper);
        });
}

// Подключение панели параметрического поиска
const searchPanelId = '#search-panel';
const searchPanelWrapper = document.querySelector(searchPanelId);

if (searchPanelWrapper) {
    import(/* webpackChunkName: "search-panel" */ '@/components/search-panel')
        .then(({default: searchPanelInit}: IDynamicImport) => {
            searchPanelInit({
                id     : searchPanelId,
                wrapper: searchPanelWrapper
            });
        });
}

// Селекты
const customSelects = Array.from(document.querySelectorAll('.j-custom-select')) as HTMLElement[];

if (customSelects.length) {
    customSelects.forEach((customSelect: HTMLElement) => {
        initCustomSelects(customSelect);
    });
}

const newsWrapper = document.querySelector<HTMLElement>('.j-news');
const newsSlider = newsWrapper?.querySelector<HTMLElement>('.j-news-slider');

if (newsWrapper && newsSlider) {
    const tabsWrapper = newsWrapper.querySelector<HTMLElement>('.j-news-tabs');
    const sliderOptions = {
        loop         : true,
        slidesPerView: 'auto',
        spaceBetween : 16,
        breakpoints  : {
            320: {
                slidesPerView: 'auto',
                spaceBetween : 12
            },
            1280: {
                slidesPerView: 2
            },
            1440: {
                slidesPerView: 3
            }
        },
        navigation: {
            nextEl: '.j-news-arrow-next',
            prevEl: '.j-news-arrow-prev'
        }
    };

    initSlider(newsSlider, {
        sliderOptions,
        tabsWrapper
    });
}

// Формы
const formWrappers = [...document.querySelectorAll('.j-form')];

formWrappers.forEach((wrapper: Element) => {
    initForm(wrapper as HTMLElement);
});

const headerEstate = document.querySelector<HTMLElement>('.j-header-estate-wrapper');

if (headerEstate) {
    import(/* webpackChunkName: "header-estate" */ '@/components/header-estate')
        .then(({default: HeaderEstate}: IDynamicImport) => {
            const openButton = document.querySelector<HTMLElement>('.j-header-estate-button');
            const headerEstateInstance = new HeaderEstate(headerEstate, openButton);

            headerEstateInstance.init();
        });
}


const location = document.querySelector('.j-location-map') as HTMLElement;

if (location) {
    import(/* webpackChunkName: "location-section" */ '@/sections/location')
        .then(({default: initLocation}: IDynamicImport) => {
            initLocation(location);
        });
}

// Подключаем раздел проекты - с табами и картой
const projects = document.querySelector<HTMLElement>('.j-projects');

if (projects) {
    initProjects(projects);
}

// Подключаем карточки проектов (могут быть как в разделе проектов с картой, так
// и по отдельности)
const projectCards = [...document.querySelectorAll<HTMLElement>('.j-projects-card')];

if (projectCards.length) {
    import(/* webpackChunkName: "project-card" */ '@/components/project-card')
        .then(({default: ProjectsCard}: IDynamicImport) => {
            projectCards.forEach((card: HTMLElement) => {
                new ProjectsCard(card).init();
            });
        });
}

const finishingSliders = [...document.querySelectorAll<HTMLElement>('.j-finishing-slider')];

finishingSliders.forEach((finishingSlider: HTMLElement) => {
    const sliderOptions = {
        loop         : true,
        autoplay     : Boolean(finishingSlider.dataset['autoplay'] === 'true'),
        slidesPerView: 3,
        spaceBetween : 16,
        speed        : 400,
        breakpoints  : {
            320: {
                slidesPerView: 1,
                spaceBetween : 0
            },
            670: {
                slidesPerView: 2,
                spaceBetween : 12
            },
            960: {
                slidesPerView: 2
            },
            1280: {
                slidesPerView: 2,
                spaceBetween : 16
            },
            1440: {
                slidesPerView: 3
            }
        },
        navigation: {
            nextEl: '.j-finishing-arrow-next',
            prevEl: '.j-finishing-arrow-prev'
        }
    };

    initSlider(finishingSlider, {sliderOptions});
});

const reviewsSliders = [...document.querySelectorAll<HTMLElement>('.j-reviews-slider')];

reviewsSliders.forEach((reviewsSlider: HTMLElement) => {
    const sliderOptions = {
        loop         : true,
        slidesPerView: 1,
        navigation   : {
            nextEl: '.j-reviews-arrow-next',
            prevEl: '.j-reviews-arrow-prev'
        }
    };

    initSlider(reviewsSlider, {sliderOptions});
});

// Табы
const tabs = [...document.querySelectorAll<HTMLElement>('.j-tabs-wrap')];

tabs.forEach((elem: HTMLElement) => {
    initTabs(elem);
});

// Попапы
const popupSelectors = [...document.querySelectorAll('.j-popup')] as HTMLElement[];

if (popupSelectors.length) {
    initPopup(popupSelectors, {removeDelay: 300});
}

// Отдельный селектор для попапов обратных звонков
const popupFacadeLinks = [...document.querySelectorAll('.j-popup-callback:not([data-init])')] as HTMLElement[];

if (popupFacadeLinks.length) {
    initCallbackPopup(popupFacadeLinks);

    popupFacadeLinks.forEach((button: HTMLElement) => {
        button.dataset['init'] = 'true';
    });
}

const popupShareLink = [...document.querySelectorAll<HTMLElement>('.j-popup-share:not([data-init])')];

if (popupShareLink.length) {
    initCallbackPopup(popupShareLink, 'popup_theme_share');

    popupShareLink.forEach((button: HTMLElement) => {
        button.dataset['init'] = 'true';
    });
}

const upButton = document.querySelector<HTMLElement>('.j-up-button');

if (upButton) {
    upButton.addEventListener('click', () => {
        window.scrollTo({
            top     : 0,
            behavior: 'smooth'
        });
    });
}

// Cкролл по якорям
const anchors = [...document.querySelectorAll('.j-anchor')] as HTMLElement[];

if (anchors.length) {
    import(/* webpackChunkName: "scrollBy" */ '@/components/scrollBy')
        .then(({default: ScrollBy}: IDynamicImport) => {
            const scrollBy: ClassTypes.IScrollBy = new ScrollBy();
            const CLASS_ACTIVE = 'is-active';

            scrollBy.init({
                takeElements: [headerWrapper],
                anchors
            });

            const setActiveAnchor = (id: string): void => {
                anchors.forEach((item: HTMLElement) => {
                    const href = item.getAttribute('href');

                    item.classList.remove(CLASS_ACTIVE);

                    if (href === id || href === `/${id}`) {
                        item.classList.add(CLASS_ACTIVE);
                    }
                });
            };

            const hash = window.location.hash;

            if (hash.length) {
                setActiveAnchor(hash);
            }

            observer.subscribe('anchorChangeSection', (id: string) => {
                setActiveAnchor(id);
            });
        });
}

const projectPageSliders = [...document.querySelectorAll<HTMLElement>('.j-project-page-slider')];

projectPageSliders.forEach((slider: HTMLElement) => {
    const sliderOptions = {
        loop         : true,
        slidesPerView: 1,
        spaceBetween : 0,
        navigation   : {
            nextEl: '.j-project-page-arrow-next',
            prevEl: '.j-project-page-arrow-prev'
        }
    };

    initSlider(slider, {sliderOptions});
});

const buyVariantsSliders = [...document.querySelectorAll<HTMLElement>('.j-buy-variants-slider')];

buyVariantsSliders.forEach((buyVariantsSlider: HTMLElement) => {
    const wide = buyVariantsSlider.dataset['wide'];
    const about = buyVariantsSlider.dataset['about'];

    const sliderOptions = {
        loop         : true,
        slidesPerView: wide ? 1 : 'auto',
        spaceBetween : 10,
        navigation   : {
            nextEl: '.j-buy-variants-arrow-next',
            prevEl: '.j-buy-variants-arrow-prev'
        },
        breakpoints: {
            670: {
                slidesPerView: about ? 'auto' : 2,
                spaceBetween : 16
            },
            960: {
                slidesPerView: about ? 'auto' : 3,
                spaceBetween : 16
            },
            1280: {
                slidesPerView: 3,
                spaceBetween : 16
            },
            1440: {
                slidesPerView: wide || about ? 3 : 4,
                spaceBetween : 16
            }
        }
    };

    initSlider(buyVariantsSlider, {sliderOptions});
});

const contentSliders = [...document.querySelectorAll<HTMLElement>('.j-content-slider')];

contentSliders.forEach((contentSlider: HTMLElement) => {
    const sliderOptions = {
        loop         : true,
        slidesPerView: 1,
        spaceBetween : 10,
        navigation   : {
            nextEl: '.j-content-arrow-next',
            prevEl: '.j-content-arrow-prev'
        }
    };

    initSlider(contentSlider, {sliderOptions});
});

const projectFinishingSlider = [...document.querySelectorAll<HTMLElement>('.j-project-finishing-slider')];

projectFinishingSlider.forEach((slider: HTMLElement) => {
    const sliderOptions = {
        loop          : true,
        slidePerView  : 1,
        allowTouchMove: false,
        navigation    : {
            nextEl: '.j-project-finishing-arrow-next',
            prevEl: '.j-project-finishing-arrow-prev'
        }
    };

    initSlider(slider, {sliderOptions});
});

const similarPremisesSliders = [...document.querySelectorAll<HTMLElement>('.j-similar-premises-slider')];

similarPremisesSliders.forEach((similarPremisesSlider: HTMLElement) => {
    const sliderOptions = {
        allowTouchMove: true,
        loop          : false,
        breakpoints   : {
            320: {
                slidesPerView: 1
            },
            670: {
                slidesPerView: 2,
                spaceBetween : 16
            },
            1280: {
                slidesPerView: 3,
                spaceBetween : 16
            },
            1920: {
                slidesPerView: 4,
                spaceBetween : 16
            }
        },
        navigation: {
            nextEl: '.j-similar-premises-arrow-next',
            prevEl: '.j-similar-premises-arrow-prev'
        }
    };

    initSlider(similarPremisesSlider, {sliderOptions});
});

const currentProjectsSliders = [...document.querySelectorAll<HTMLElement>('.j-current-projects__slider')];

currentProjectsSliders.forEach((currentProjectsSlider: HTMLElement) => {
    const sliderOptions = {
        loop         : true,
        slidesPerView: 'auto',
        spaceBetween : 10,
        breakpoints  : {
            320: {
                slidesPerView: 'auto',
                spaceBetween : 10
            },
            670: {
                slidesPerView: 'auto',
                spaceBetween : 12
            },
            1280: {
                slidesPerView: 'auto',
                spaceBetween : 16
            },
            1440: {
                slidesPerView: 5
            }
        },
        navigation: {
            nextEl: '.j-current-project-arrow-next',
            prevEl: '.j-current-project-arrow-prev'
        }
    };

    initSlider(currentProjectsSlider, {sliderOptions});
});

const historySliders = [...document.querySelectorAll<HTMLElement>('.j-history-slider')];

if (historySliders.length) {
    import(/* webpackChunkName: "history-slider" */ '@/sections/history')
        .then(({default: HistorySlider}: IDynamicImport) => {
            historySliders.forEach((historySlider: HTMLElement) => {
                new HistorySlider(historySlider).init();
            });
        });
}


const plans = [...document.querySelectorAll<HTMLElement>('.j-plans')];

if (plans.length) {
    import(/* webpackChunkName: "plans" */ '@/sections/plans')
        .then(({default: Plans}: IDynamicImport) => {
            plans.forEach((elem: HTMLElement) => {
                new Plans(elem).init();
            });
        });
}

const videoWrappers = [...document.querySelectorAll<HTMLElement>('.j-video-wrapper')];

if (videoWrappers.length) {
    videoWrappers.forEach((wrapper: HTMLElement) => {
        initVideo(wrapper);
    });
}

// Шеринг
const shareElements = [...document.querySelectorAll('.j-share')] as HTMLElement[];

if (shareElements.length) {
    initShare(shareElements);
}

// Ипотечный калькулятор
const mortgageCalculator = document.querySelector('.j-mortgage-calculator') as HTMLElement;

if (mortgageCalculator) {
    import(/* webpackChunkName: "mortgage-calculator" */ '@/components/mortgage-calculator')
        .then(({default: MortgageCalculator}: IDynamicImport) => {
            const calculator: ClassTypes.IMortgageCalculator = new MortgageCalculator(mortgageCalculator);

            calculator.init();

            // Отображения блока и подстановка месячного платежа ипотеки в карточке квартиры
            const priceMortgageWrappers = [...document.querySelectorAll('.j-price__mortgage')];
            const monthlyPayment = mortgageCalculator.dataset['minMonthly'];

            if (monthlyPayment?.length) {
                priceMortgageWrappers.forEach((wrapper: Element) => {
                    import(/* webpackChunkName: "price-monthly-payment" */ '@/components/price')
                        .then(({default: Price}: IDynamicImport) => {
                            const priceMonthlyPayment: ClassTypes.IPrice = new Price({
                                element: wrapper,
                                payment: monthlyPayment,
                                href   : mortgageCalculator.closest('section')?.id || null
                            });

                            priceMonthlyPayment.init();
                        });
                });
            }
        });
}

// Details Accordion
const details = [...document.querySelectorAll('.j-details')];

if (details.length) {
    import(/* webpackChunkName: "details-accordion" */ '@/components/details')
        .then(({default: DetailsAccordion}: IDynamicImport) => {
            details.forEach((element: Element) => {
                const accordion: ClassTypes.IDetailsAccordion = new DetailsAccordion();

                accordion.init(element as HTMLDetailsElement);
            });
        });
}

// Ход строительства
const progress = document.querySelector('.j-progress');

if (progress) {
    import(/* webpackChunkName: "progress" */ '@/sections/progress')
        .then(({default: Progress}: IDynamicImport) => {
            new Progress(progress).init();
        });
}

// Инициализация планоплана внутри табов
const flatPlan = document.querySelector<HTMLElement>('.j-flat-plan');

if (flatPlan) {
    import(/* webpackChunkName: "flat-block" */ '@/sections/flat-plan')
        .then(({default: FlatPlan}: IDynamicImport) => {
            new FlatPlan(flatPlan).init();
        });
}

const similarPremises = [...document.querySelectorAll<HTMLElement>('.j-similar-premise')];

if (similarPremises.length) {
    initSimilarPremises(similarPremises);
}

// Карта в контактах
const contactsMap = document.querySelector('.j-contacts-map') as HTMLElement;

if (contactsMap) {
    import(/* webpackChunkName: "contacts-map" */ '@/sections/contacts-map')
        .then(({default: ContactsMap}: IDynamicImport) => {
            new ContactsMap(contactsMap).init();
        });
}


const levelsSliders = [...document.querySelectorAll<HTMLElement>('.j-levels-slider')];

levelsSliders.forEach((levelsSlider: HTMLElement) => {
    const sliderOptions = {
        loop         : false,
        slidesPerView: 'auto',
        spaceBetween : 16,
        breakpoints  : {
            320: {
                enabled: false
            },
            670: {
                enabled: true
            },
            1600: {
                enabled: false
            }
        }
    };

    initSlider(levelsSlider, {sliderOptions});
});

const tendersSliders = [...document.querySelectorAll<HTMLElement>('.j-tenders-partners-slider')];

tendersSliders.forEach((tendersSlider: HTMLElement) => {
    const sliderOptions = {
        loop         : false,
        slidesPerView: 'auto',
        spaceBetween : 16,
        navigation   : {
            nextEl: '.j-tenders-partners-arrow-next',
            prevEl: '.j-tenders-partners-arrow-prev'
        }
    };

    initSlider(tendersSlider, {sliderOptions});
});

const advantagesSliders = [...document.querySelectorAll<HTMLElement>('.j-advantages-slider')];

advantagesSliders.forEach((advantagesSlider: HTMLElement) => {
    const sliderOptions = {
        loop         : true,
        effect       : 'fade',
        slidesPerView: 1,
        spaceBetween : 0,
        speed        : 800,
        navigation   : {
            nextEl: '.j-advantages-arrow-next',
            prevEl: '.j-advantages-arrow-prev'
        }
    };

    initSlider(advantagesSlider, {sliderOptions});
});

// Новости
const newsList = document.querySelector('.j-news-list') as HTMLElement;

if (newsList) {
    import(/* webpackChunkName: "news-list" */ '@/components/news-list')
        .then(({default: NewsList}: IDynamicImport) => {
            new NewsList().init(newsList);
        });
}

// Сохраняем просмотренные квартиры
const flatCard = document.querySelector('.j-flat-head') as HTMLElement;

if (flatCard) {
    const cardCookieName = 'viewedFlatsIds';
    const currentCardId = flatCard.dataset['id'];

    if (currentCardId) {
        if (Utils.getCookie(cardCookieName)) {
            const currentIds = JSON.parse(Utils.getCookie(cardCookieName) || '[]');

            if (currentIds.includes(currentCardId)) {
                const index = currentIds.findIndex((item: string) => {
                    return item === currentCardId;
                });

                // eslint-disable-next-line
                if (index !== -1 && index !== 0) {
                    const tempId = currentIds[index];

                    currentIds.splice(index, 1);
                    currentIds.unshift(tempId);

                    Utils.setCookie(cardCookieName, JSON.stringify(currentIds));
                }
            } else {
                currentIds.unshift(currentCardId);

                Utils.setCookie(cardCookieName, JSON.stringify(currentIds.slice(0, 50)));
            }
        } else {
            const cardsIdList = [currentCardId];

            Utils.setCookie(cardCookieName, JSON.stringify(cardsIdList));
        }
    }
}

const headerFavorites = [...document.querySelectorAll<HTMLElement>('.j-header-favorites-count')];

if (headerFavorites.length) {
    import(/* webpackChunkName: "favorites" */ '@/components/favorites')
        .then(({default: changeHeaderFavorite}: IDynamicImport) => {
            headerFavorites.forEach((headerFavorite: HTMLElement) => {
                changeHeaderFavorite(headerFavorite);
            });
        });
}

const favoriteButtons = [...document.querySelectorAll<HTMLElement>('.j-favorite')];

if (favoriteButtons.length) {
    import(/* webpackChunkName: "flat-card" */ '@/components/flat-card')
        .then(({default: initFlatPageButtons}: IDynamicImport) => {
            favoriteButtons.forEach((favoriteButton: HTMLElement) => {
                initFlatPageButtons(favoriteButton);
            });
        });
}

const bakeryBlock = document.querySelector<HTMLElement>('.j-mouse-bakery-animation');

if (bakeryBlock) {
    const elements: HTMLCollection = bakeryBlock.children;

    document.addEventListener('mousemove', (event: MouseEvent) => {
        for (let i = 0; i < elements.length; i++) {
            const el = elements[i] as HTMLElement;

            // Добавляем небольшой сдвиг
            el.style.transform = `translate(${event.clientX * 0.025}px, ${event.clientY * 0.02}px)`;
        }
    });
}

const pdfDownloadButtons = [...document.querySelectorAll<HTMLLinkElement>('.j-download-pdf')];

pdfDownloadButtons.forEach((button: HTMLLinkElement) => {
    initDownload(button);
});

const rotaryCards = [...document.querySelectorAll<HTMLElement>('.j-rotary-item')];

if (rotaryCards.length) {
    initRotation(rotaryCards);
}

const releases = document.querySelector<HTMLElement>('.j-collapse');

if (releases) {
    initAdaptive(releases);
}

const careerNum = document.querySelector<HTMLElement>('.j-career-button-num');
const vacanciesNum = document.querySelector<HTMLElement>('.j-vacancies-num');

if (careerNum && vacanciesNum) {
    careerNum.innerText = vacanciesNum.innerText;
}
